// File: src/Partials/Universal/Dashboard/Components/BookingTable.jsx
import React, { useState, useMemo } from "react";
import {
  Clock,
  User,
  Hotel
} from "lucide-react";

import { GetTodayBookingList } from "adminApi/DashboardApi/Dashboard";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../json/TodayBookingFields.json";

// ─────────────────────────────────────────────────────
// Fake Data Generator
// ─────────────────────────────────────────────────────
const getFakeMeta = (page = 1, limit = 10) => ({
  page,
  limit,
  totalDocs: 156,
  totalPages: Math.ceil(156 / limit),
  prevPage: page > 1 ? page - 1 : null,
  nextPage: page < Math.ceil(156 / limit) ? page + 1 : null
});

// ─────────────────────────────────────────────────────
// Helpers (Prepared Data Function like FilterStatus)
// ─────────────────────────────────────────────────────
const getFakeBookingData = (page = 1, limit = 10) => {
  const fakeNames = [
    'John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis', 'Robert Wilson',
    'Lisa Anderson', 'David Martinez', 'Jennifer Taylor', 'James Thomas', 'Maria Garcia',
    'William Jones', 'Patricia Miller', 'Christopher Lee', 'Linda White', 'Daniel Harris'
  ];

  const roomTypes = ['Standard Single', 'Standard Double', 'Deluxe Single', 'Deluxe Double', 'Suite', 'Family Room'];
  const statuses = [0, 1, 2, 3, 4, 5];

  return Array.from({ length: limit }, (_, idx) => {
    const bookedId = (page - 1) * limit + idx + 1;
    const checkinDate = new Date();
    checkinDate.setDate(checkinDate.getDate() + Math.floor(Math.random() * 7));
    const checkoutDate = new Date(checkinDate);
    checkoutDate.setDate(checkoutDate.getDate() + Math.floor(Math.random() * 5) + 1);

    const totalAmount = Math.floor(Math.random() * 3000) + 500;
    const paidAmount = Math.floor(totalAmount * (Math.random() * 0.8 + 0.2));

    return {
      booked_id: bookedId,
      customerName: fakeNames[Math.floor(Math.random() * fakeNames.length)],
      roomType: roomTypes[Math.floor(Math.random() * roomTypes.length)],
      checkin_date: checkinDate.toISOString(),
      checkout_date: checkoutDate.toISOString(),
      paidAmount: paidAmount,
      dueAmount: totalAmount - paidAmount,
      booking_status: statuses[Math.floor(Math.random() * statuses.length)]
    };
  });
};

const prepareBookingData = (data, meta) => {
  return data.map((item, idx) => {
    const checkin = new Date(item.checkin_date);
    const checkout = new Date(item.checkout_date);
    const nightCount = Math.ceil((checkout - checkin) / (1000 * 60 * 60 * 24)) || 1;

    // Formatting date helper
    const formatDate = (dateStr) => {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
    };

    // Currency helper
    const formatCurrency = (val) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
      }).format(val || 0);
    };

    // Status Badge helper
    const statusMap = {
      0: { label: "Pending", cls: "warning" },
      1: { label: "Cancelled", cls: "danger" },
      2: { label: "Confirmed", cls: "success" },
      3: { label: "Finished", cls: "info" },
      4: { label: "Check-in", cls: "primary" },
      5: { label: "Check-out", cls: "secondary" },
    };
    const s = statusMap[Number(item.booking_status)] ?? { label: "Unknown", cls: "secondary" };

    return {
      ...item,
      // Overriding fields with JSX as per premium design requirements
      customerName: (
        <div className="d-flex align-items-center gap-3">
          <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
            <User size={20} />
          </div>
          <div>
            <p className="mb-0 fw-bold text-nowrap">{item.customerName}</p>
            <p className="mb-0 small text-muted">ID: {880000 + (meta.page - 1) * meta.limit + idx}</p>
          </div>
        </div>
      ),
      roomType: (
        <div className="d-flex align-items-center gap-2 text-nowrap">
          <Hotel size={16} className="text-muted" />
          <span className="fw-medium">{item.roomType || 'Standard'}</span>
        </div>
      ),
      nights: <span className="fw-bold text-primary">{nightCount}</span>,
      duration: (
        <div className="d-flex align-items-center gap-2 small text-muted text-nowrap">
          <Clock size={14} />
          {formatDate(item.checkin_date)} - {formatDate(item.checkout_date)}
        </div>
      ),
      paidAmount: <span className="fw-bold text-dark">{formatCurrency(item.paidAmount)}</span>,
      dueAmount: <span className="fw-bold text-danger">{formatCurrency(item.dueAmount)}</span>,
      status: (
        <div className={`d-flex align-items-center gap-2 text-${s.cls} fw-medium small`}>
          <div className={`bg-${s.cls}`} style={{ width: '6px', height: '6px', borderRadius: '50%' }}></div>
          {s.label}
        </div>
      )
    };
  });
};

// ─────────────────────────────────────────────────────
// Booking Table Component (Matches UserListTable Flow)
// ─────────────────────────────────────────────────────
const BookingTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const sizeOptions = [10, 20, 50, 100];

  const { tabledata } = fields;

  // 1. Fetch Hook with fallback
  const { data, meta, loading, error, refetch } = usePaginatedFetch(
    GetTodayBookingList,
    {
      page,
      limit,
      search: debouncedSearch,
      title: "Today's Guests",
    },
    // Fallback data when API fails
    () => ({
      data: getFakeBookingData(page, limit),
      meta: getFakeMeta(page, limit)
    })
  );

  // 2. Auto-generate display fields 
  const displayFields = useMemo(() => generateDisplayFields(tabledata), [tabledata]);

  // 3. Process data (like FilterStatus)
  const filterdata = useMemo(() => prepareBookingData(data, meta), [data, meta]);

  // 4. Table Hook (The core "Code Flow" requirement)
  const { formattedData, columns } = useTableConfig({
    data: filterdata,
    page,
    limit,
    canShowAction: true, // Show the View/Action button
    refetch,
    config: {
      entityName: "Booking",
      editUrl: "view-booking", // Matches the route in HotelRoutes.jsx
      idKey: "booked_id",
      displayFields,
    },
    IsEdit: false,        // Hide Edit button, we just want to view
    isView: false,         // Show View button
    IsDelete: false       // Hide Delete button for dashboard
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => { setPage(p) };

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={formattedData}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default BookingTable;